import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, ExternalLink, Boxes, Users, Building2, UserCog, Plug, CheckCircle2, Layers, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import SEO from "../components/SEO";
import JsonLd from "../components/JsonLd";
import { SITE_URL } from "../components/OrganizationSchema";
import FinalCta from "../sections/FinalCta";
import Markdown from "../components/Markdown";
import { matchServicesFor, matchPostsForKeywords } from "../lib/related";

// inline helper removed — now using shared Markdown component.

export default function ProjectDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState("");
  const [lightbox, setLightbox] = useState(null);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    setItem(null);
    api.get(`/projects/${slug}`).then((r) => setItem(r.data)).catch(() => setErr("Proje bulunamadı"));
  }, [slug]);

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data || [])).catch(() => {});
  }, []);

  if (err) {
    return (
      <div className="container-x py-32 text-center">
        <h1 className="h2-section">Proje bulunamadı</h1>
        <Link to="/projeler" className="mt-6 inline-flex btn-secondary">Projelere dön</Link>
      </div>
    );
  }
  if (!item) return <div className="container-x py-32 text-center text-[#334155]">Yükleniyor...</div>;

  const gallery = item.gallery || [];
  const faq = (item.faq || []).filter((x) => x?.q && x?.a);
  const matchText = `${item.title} ${item.sector || ""} ${item.need || ""} ${item.solution || ""}`;
  const relatedServices = matchServicesFor(matchText, item.tags, 3);
  const relatedPosts = matchPostsForKeywords(posts, [...(item.tags || []), item.sector, item.title].filter(Boolean), 3);
  const projectUrl = `${SITE_URL}/projeler/${item.slug}`;
  const jsonLdGraph = [
    {
      "@type": "Article",
      "@id": `${projectUrl}#article`,
      headline: item.title,
      description: item.seo_description || item.need || "",
      ...(item.cover_image ? { image: [item.cover_image] } : {}),
      ...(item.sector ? { about: item.sector } : {}),
      ...(item.tags?.length ? { keywords: item.tags.join(", ") } : {}),
      author: { "@id": `${SITE_URL}/#organization` },
      publisher: { "@id": `${SITE_URL}/#organization` },
      mainEntityOfPage: projectUrl,
      inLanguage: "tr-TR",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Projeler", item: `${SITE_URL}/projeler` },
        { "@type": "ListItem", position: 3, name: item.title, item: projectUrl },
      ],
    },
  ];
  if (faq.length) {
    jsonLdGraph.push({
      "@type": "FAQPage",
      "@id": `${projectUrl}#faq`,
      mainEntity: faq.map((x) => ({
        "@type": "Question",
        name: x.q,
        acceptedAnswer: { "@type": "Answer", text: x.a },
      })),
    });
  }
  const projectJsonLd = { "@context": "https://schema.org", "@graph": jsonLdGraph };

  return (
    <>
      <SEO title={item.seo_title || `${item.title} | Dijital Roket`} description={item.seo_description || item.summary || item.need} image={item.cover_image} />
      <JsonLd id="project" data={projectJsonLd} />
      <section className="relative overflow-hidden bg-[#07111F] text-white">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute -top-20 left-1/4 h-[420px] w-[420px] rounded-full bg-[#2563EB]/25 blur-[120px]" />
        <div className="container-x relative pt-24 pb-16">
          <Link to="/projeler" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> Tüm Projeler
          </Link>
          <nav aria-label="breadcrumb" className="mt-4">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/50" data-testid="project-breadcrumb">
              <li><Link to="/" className="hover:text-white">Ana Sayfa</Link></li>
              <li aria-hidden="true">/</li>
              <li><Link to="/projeler" className="hover:text-white">Projeler</Link></li>
              <li aria-hidden="true">/</li>
              <li className="text-white/80" aria-current="page">{item.title}</li>
            </ol>
          </nav>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            {item.badge && (
              <span
                data-testid="project-badge"
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${
                  item.project_type === "GERÇEK PROJE"
                    ? "border border-[#10B981]/40 bg-[#10B981]/10 text-[#34D399]"
                    : "border border-[#22D3EE]/40 bg-[#22D3EE]/10 text-[#22D3EE]"
                }`}
              >
                {item.badge}
              </span>
            )}
            <span className="eyebrow-light">{item.sector}</span>
          </div>
          <h1 className="mt-3 h1-display text-white max-w-4xl">{item.title}</h1>
          {item.subtitle && (
            <p className="mt-4 max-w-3xl text-lg sm:text-xl font-medium text-[#22D3EE]/90 leading-snug">{item.subtitle}</p>
          )}
          {item.summary && (
            <p className="mt-5 max-w-3xl text-base text-white/70 leading-relaxed">{item.summary}</p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            {(item.tags || []).map((t) => (
              <span key={t} className="rounded-md border border-white/15 bg-white/5 px-2.5 py-1 text-xs uppercase tracking-wide text-white/70">{t}</span>
            ))}
          </div>
          {item.external_url && (
            <a
              href={item.external_url}
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-lg border border-white/15 bg-white/5 backdrop-blur px-4 py-2 text-sm text-white hover:bg-white/10 transition"
            >
              <ExternalLink className="h-3.5 w-3.5" /> {item.external_url.replace(/^https?:\/\//, "")}
            </a>
          )}
        </div>
      </section>

      {item.cover_image && (
        <div className="container-x -mt-10">
          <img
            src={item.cover_image}
            alt={item.title}
            className="rounded-2xl border border-slate-200 shadow-xl w-full aspect-[16/9] object-cover bg-[#07111F]"
          />
        </div>
      )}

      {/* Summary cards */}
      <section className="pt-16 pb-8 bg-white">
        <div className="container-x grid gap-6 lg:grid-cols-3">
          {[
            { label: "İhtiyaç", body: item.need },
            { label: "Çözüm", body: item.solution },
            { label: "Sonuç", body: item.result },
          ].map((b) => (
            <div key={b.label} className="card-elevate p-7">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#2563EB]">{b.label}</div>
              <p className="mt-3 text-[15px] leading-relaxed text-[#334155]">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Full content */}
      {item.content && (
        <article className="pb-12 bg-white">
          <div className="container-x max-w-3xl"><Markdown source={item.content} /></div>
        </article>
      )}

      <EnterpriseSections item={item} />

      {/* Gallery */}
      {gallery.length > 0 && (
        <section className="pb-20 bg-white" data-testid="project-gallery">
          <div className="container-x">
            <div className="max-w-3xl mb-10">
              <span className="eyebrow">Proje Galerisi</span>
              <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold text-[#07111F]">
                Çalışmadan Kareler
              </h2>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {gallery.map((g, i) => (
                <motion.button
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
                  onClick={() => setLightbox(g)}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-[#F8FAFC] aspect-[4/3] text-left"
                  data-testid={`gallery-item-${i}`}
                >
                  <img
                    src={g.url}
                    alt={g.caption || `${item.title} - görsel ${i + 1}`}
                    loading="lazy"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {g.caption && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#07111F]/90 via-[#07111F]/50 to-transparent p-4">
                      <p className="text-sm font-medium text-white leading-snug">{g.caption}</p>
                    </div>
                  )}
                </motion.button>
              ))}
            </div>
          </div>
        </section>
      )}

      {faq.length > 0 && (
        <section className="pb-16 bg-white" data-testid="project-faq">
          <div className="container-x max-w-3xl">
            <span className="eyebrow">Sık Sorulan Sorular</span>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl font-bold text-[#07111F]">
              Bu proje hakkında merak edilenler
            </h2>
            <dl className="mt-8 space-y-4">
              {faq.map((x, i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-6"
                  data-testid={`faq-item-${i}`}
                >
                  <dt className="font-heading text-base font-bold text-[#07111F]">{x.q}</dt>
                  <dd className="mt-2 text-[15px] leading-relaxed text-[#334155]">{x.a}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>
      )}

      {/* Internal links: related services */}
      {relatedServices.length > 0 && (
        <section className="pb-4 bg-white" data-testid="project-related-services">
          <div className="container-x">
            <h2 className="font-heading text-xl font-bold text-[#07111F]">Bu projeyle ilgili hizmetlerimiz</h2>
            <div className="mt-4 flex flex-wrap gap-3">
              {relatedServices.map((s) => (
                <Link key={s.slug} to={`/hizmetler/${s.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-[#F8FAFC] px-4 py-2 text-sm font-medium text-[#334155] hover:border-[#2563EB]/40 hover:text-[#07111F] transition" data-testid={`project-rel-service-${s.slug}`}>
                  {s.navLabel} çözümlerimizi inceleyin <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Internal links: related posts */}
      {relatedPosts.length > 0 && (
        <section className="pb-12 bg-white" data-testid="project-related-posts">
          <div className="container-x">
            <h2 className="font-heading text-xl font-bold text-[#07111F]">İlgili yazılar</h2>
            <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedPosts.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group card-elevate p-5" data-testid={`project-rel-post-${p.slug}`}>
                  <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#2563EB]">{p.category}</span>
                  <h3 className="mt-1.5 font-heading text-base font-semibold text-[#07111F] leading-snug line-clamp-2">{p.title}</h3>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#2563EB] group-hover:gap-1.5 transition-all">Oku <ArrowRight className="h-3.5 w-3.5" /></span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="container-x mb-16 flex flex-col sm:flex-row items-center justify-between gap-6 rounded-3xl border border-slate-200 bg-[#F8FAFC] p-8">
        <div>
          <h3 className="h3-card">Benzer bir proje sizin için de mümkün.</h3>
          <p className="mt-2 text-sm text-[#334155]">Bu vakaya benzer bir ihtiyaç için kapsam sohbeti planlayalım.</p>
        </div>
        <Link to="/proje-talep" className="btn-primary">Projemi Analiz Edin <ArrowRight className="h-4 w-4" /></Link>
      </div>

      <FinalCta />

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={() => setLightbox(null)}
          data-testid="gallery-lightbox"
        >
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.url} alt={lightbox.caption} className="w-full max-h-[80vh] object-contain rounded-xl" />
            {lightbox.caption && (
              <p className="mt-4 text-center text-sm text-white/80">{lightbox.caption}</p>
            )}
            <button
              onClick={() => setLightbox(null)}
              className="mt-3 mx-auto block text-xs text-white/60 hover:text-white"
            >
              Kapatmak için herhangi bir yere tıklayın
            </button>
          </div>
        </div>
      )}
    </>
  );
}


function EnterpriseSections({ item }) {
  const about = (item.about || "").split("\n\n").filter(Boolean);
  const modules = item.modules || [];
  const flow = item.flow || [];
  const features = item.features || [];
  const roles = item.user_roles || [];
  const execBenefits = item.executive_benefits || [];
  const empBenefits = item.employee_benefits || [];
  const integrations = item.integrations || [];

  const hasAnything =
    about.length || modules.length || flow.length || features.length || roles.length ||
    execBenefits.length || empBenefits.length || integrations.length || item.scalability || item.cta_title;

  if (!hasAnything) return null;

  return (
    <>
      {/* Proje Hakkında */}
      {about.length > 0 && (
        <section className="pb-14 bg-white" data-testid="project-about">
          <div className="container-x max-w-3xl">
            <span className="eyebrow">Proje Hakkında</span>
            <div className="mt-5 space-y-4">
              {about.map((p, i) => (
                <p key={i} className="text-[15px] sm:text-base leading-relaxed text-[#334155]">{p}</p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sistem Mimarisi (modüller) */}
      {modules.length > 0 && (
        <section className="section bg-[#F8FAFC]" data-testid="project-modules">
          <div className="container-x">
            <div className="max-w-2xl">
              <span className="eyebrow">Sistem Mimarisi</span>
              <h2 className="mt-3 h2-section">Temel Modüller</h2>
            </div>
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {modules.map((m, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.4, delay: (i % 3) * 0.05 }}
                  className="rounded-2xl border border-slate-200 bg-white p-6"
                  data-testid={`project-module-${i}`}
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#07111F] text-[#22D3EE]">
                    <Boxes className="h-4 w-4" strokeWidth={1.8} />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-semibold text-[#07111F]">{m.title}</h3>
                  {m.desc && <p className="mt-2 text-sm leading-relaxed text-[#334155]">{m.desc}</p>}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Nasıl Çalışır? (flow) */}
      {flow.length > 0 && (
        <section className="section bg-[#07111F] text-white relative overflow-hidden" data-testid="project-flow">
          <div className="absolute inset-0 bg-grid opacity-30" />
          <div className="container-x relative">
            <div className="max-w-2xl">
              <span className="eyebrow-light">Nasıl Çalışır?</span>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-white">Uçtan Uca Akış</h2>
            </div>
            <div className="mt-12 flex flex-wrap items-stretch gap-3">
              {flow.map((step, i) => (
                <div key={i} className="flex items-center gap-3">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: i * 0.06 }}
                    className="rounded-xl border border-white/10 bg-white/[0.04] px-5 py-4 backdrop-blur min-w-[130px] text-center"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#22D3EE]/70">Adım {i + 1}</div>
                    <div className="mt-1 text-sm font-semibold text-white">{step}</div>
                  </motion.div>
                  {i < flow.length - 1 && <ChevronRight className="h-5 w-5 text-white/30 shrink-0" />}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Öne çıkan özellikler & kullanıcı rolleri */}
      {(features.length > 0 || roles.length > 0) && (
        <section className="section bg-white" data-testid="project-features">
          <div className="container-x grid gap-12 lg:grid-cols-2">
            {features.length > 0 && (
              <div>
                <span className="eyebrow">Öne Çıkan Özellikler</span>
                <ul className="mt-6 space-y-3">
                  {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#2563EB]/10 text-[#2563EB]">
                        <CheckCircle2 className="h-4 w-4" />
                      </span>
                      <span className="text-[15px] text-[#334155]">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {roles.length > 0 && (
              <div>
                <span className="eyebrow">Kullanıcı Rolleri</span>
                <div className="mt-6 flex flex-wrap gap-2.5">
                  {roles.map((r, i) => (
                    <span key={i} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-[#F8FAFC] px-4 py-2 text-sm font-medium text-[#07111F]">
                      <Users className="h-3.5 w-3.5 text-[#2563EB]" /> {r}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Yönetici / Çalışan için */}
      {(execBenefits.length > 0 || empBenefits.length > 0) && (
        <section className="section bg-[#F8FAFC]" data-testid="project-benefits">
          <div className="container-x grid gap-6 lg:grid-cols-2">
            {execBenefits.length > 0 && (
              <div className="rounded-3xl border border-slate-200 bg-white p-8">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#07111F] text-[#22D3EE]">
                  <Building2 className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-heading text-xl font-bold text-[#07111F]">Yönetici İçin</h3>
                <ul className="mt-5 space-y-3">
                  {execBenefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-[15px] text-[#334155]">
                      <ChevronRight className="h-4 w-4 mt-1 shrink-0 text-[#2563EB]" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {empBenefits.length > 0 && (
              <div className="rounded-3xl border border-slate-200 bg-white p-8">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#07111F] text-[#22D3EE]">
                  <UserCog className="h-5 w-5" />
                </span>
                <h3 className="mt-5 font-heading text-xl font-bold text-[#07111F]">Çalışan İçin</h3>
                <ul className="mt-5 space-y-3">
                  {empBenefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-[15px] text-[#334155]">
                      <ChevronRight className="h-4 w-4 mt-1 shrink-0 text-[#2563EB]" /> {b}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Entegrasyonlar & Ölçeklenebilirlik */}
      {(integrations.length > 0 || item.scalability) && (
        <section className="section bg-white" data-testid="project-integrations">
          <div className="container-x grid gap-12 lg:grid-cols-2 lg:items-start">
            {integrations.length > 0 && (
              <div>
                <span className="eyebrow">Entegrasyon Kabiliyeti</span>
                <h2 className="mt-3 font-heading text-2xl font-bold text-[#07111F]">Mevcut Sistemlerinizle Konuşur</h2>
                <div className="mt-6 flex flex-wrap gap-2.5">
                  {integrations.map((it, i) => (
                    <span key={i} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-[#F8FAFC] px-3.5 py-2 text-sm font-medium text-[#334155]">
                      <Plug className="h-3.5 w-3.5 text-[#2563EB]" /> {it}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {item.scalability && (
              <div>
                <span className="eyebrow">Ölçeklenebilirlik</span>
                <h2 className="mt-3 font-heading text-2xl font-bold text-[#07111F]">Küçük Başlar, Büyür</h2>
                <div className="mt-6 flex items-start gap-3 rounded-2xl border border-slate-200 bg-[#F8FAFC] p-6">
                  <Layers className="h-5 w-5 mt-0.5 shrink-0 text-[#2563EB]" />
                  <p className="text-[15px] leading-relaxed text-[#334155]">{item.scalability}</p>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Proje CTA */}
      {(item.cta_title || item.cta_text) && (
        <section className="pb-16 bg-white" data-testid="project-cta">
          <div className="container-x">
            <div className="relative overflow-hidden rounded-3xl bg-[#07111F] p-10 sm:p-14 text-center">
              <div className="absolute inset-0 bg-grid opacity-25" />
              <div className="absolute -top-16 left-1/3 h-52 w-52 rounded-full bg-[#2563EB]/25 blur-[100px]" />
              <div className="relative max-w-2xl mx-auto">
                {item.cta_title && (
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white leading-snug">{item.cta_title}</h2>
                )}
                {item.cta_text && <p className="mt-4 text-white/70">{item.cta_text}</p>}
                <Link to="/proje-talep" className="btn-primary mt-8 inline-flex">
                  {item.cta_button || "Projemizi Konuşalım"} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
